import { AppPage, MenuItem } from 'common/models/page-config.models';
import { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { CAPTURE_ROUTES, ANALYZE_ROUTES, COACH_ROUTES } from 'routes/routes.const';
import { useModuleProjectContext } from 'common/contexts/useModuleProjectContext';
import { buildTree } from './utils';
import { useCompareUrlWithProjectId } from './route';

interface UseNavigationMenus {
  currentPageMenuItem: MenuItem | undefined;
  currentSubPageMenuItem: MenuItem | undefined;
  currentTabMenuItem: MenuItem | undefined;
  currentPageDetails: MenuItem | undefined;
  topMenuItems: MenuItem[];
  tabMenuItems: MenuItem[];
  pageTree: MenuItem[];
  hasElsieMenuItem: boolean;
  hasTeamDetailPage: boolean;
  captureOnly: boolean;
  projectPages: AppPage[];
}

function useNavigationMenus(): UseNavigationMenus {
  const { compareUrl } = useCompareUrlWithProjectId();
  const { page, subPage, tab } = useParams();
  const { activeProject } = useModuleProjectContext();
  const projectPages = activeProject?.pages?.filter((page) => page.Route) ?? [];
  const pageTree: MenuItem[] = useMemo(
    () => buildTree(activeProject?.pages ?? []),
    [activeProject]
  );

  const currentPageMenuItem: MenuItem | undefined = pageTree.find((menu) => {
    return compareUrl(menu.route as string, { startsWith: true });
  });

  const topMenuItems: MenuItem[] =
    currentPageMenuItem?.children
      ?.reduce((acc: MenuItem[], child: MenuItem) => {
        if (child.isTabBar) {
          return acc;
        }

        if (child.route && child.isMenuBar) {
          acc.push(child);
        }

        if (!child.route && child.children?.length) {
          acc.push(...child.children);
        }

        return acc;
      }, [])
      .sort((a, b) => a.sortOrder - b.sortOrder) ?? [];

  const currentSubPageMenuItem: MenuItem | undefined = topMenuItems.find((menu) => {
    return menu.route?.includes(`${page}/${subPage}`);
  });

  const tabMenuItems: MenuItem[] = useMemo(
    () =>
      currentSubPageMenuItem?.children ??
      currentPageMenuItem?.children?.filter((child) => child.isTabBar) ??
      [],
    [currentSubPageMenuItem?.children, currentPageMenuItem?.children]
  );

  const currentTabMenuItem: MenuItem | undefined = tabMenuItems.find((menu) => {
    return menu.route?.includes(`${tab ?? subPage}`);
  });
  const hasTeamDetailPage = !!projectPages.find(
    (page) => COACH_ROUTES.team_detail_interaction_kpis === page.Route
  );
  const hasElsieMenuItem = !!projectPages.find((page) => ANALYZE_ROUTES.elsieai === page.Route);
  const captureOnly = projectPages.length === 1 && projectPages[0].Route === CAPTURE_ROUTES.root;

  return {
    currentPageMenuItem,
    currentSubPageMenuItem,
    currentTabMenuItem,
    currentPageDetails: currentTabMenuItem ?? currentSubPageMenuItem ?? currentPageMenuItem,
    topMenuItems,
    tabMenuItems,
    pageTree,
    hasElsieMenuItem,
    hasTeamDetailPage,
    captureOnly,
    projectPages,
  };
}

export default useNavigationMenus;
